import axios from "axios";

const AUTH_URL = process.env.REACT_APP_AUTH_URL;

const SESSION_EXPIRED =
  "Session expired. Please log in again for continued access";

export const TOKEN_KEYS = {
  ACCESS_TOKEN: "accessToken", // JWT
  REFRESH_TOKEN: "ref_token", // JWT
  SSO_MEMBER_ID: "member_Id", // UUID (IMPORTANT)
};

export const axiosInstance = axios.create({
  baseURL: AUTH_URL,
});

// ───────────── NEW: VALIDATE TOKEN API ─────────────
const validateToken = async (accessToken) => {
  try {
    const res = await axios.post(`${AUTH_URL}/auth/validate-token`, {
      parameters: {
        accessToken: accessToken,
      },
    });

    return res.data?.body?.tokenExpired === true;
  } catch (err) {
    console.error("Token validation failed:", err);
    return true; // assume expired for safety
  }
};

// ───────────── REQUEST ─────────────
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
    const language = localStorage.getItem("language");

    if (token) {
      config.headers.Authorization = token; // as per your backend
    }

    if (language) {
      config.headers["Accept-Language"] = language;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ───────────── RESPONSE ─────────────
axiosInstance.interceptors.response.use(
  async (response) => {
    const originalRequest = response.config || {};
    originalRequest.headers = originalRequest.headers || {};

    const isSessionExpired =
      response.data?.statusCodeValue === 401 ||
      response.data?.body?.error === SESSION_EXPIRED ||
      response.data?.statusCode === "UNAUTHORIZED";

    // ✅ Normal response
    if (!isSessionExpired) {
      return response;
    }

    // ❌ Prevent infinite loop
    if (originalRequest.url?.includes("/auth/sso/refresh")) {
      clearAndRedirect();
      return Promise.reject(response);
    }

    // ❌ Already retried
    if (originalRequest._retry) {
      clearAndRedirect();
      return Promise.reject(response);
    }

    originalRequest._retry = true;
    const accessToken = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
    const memberId = localStorage.getItem(TOKEN_KEYS.SSO_MEMBER_ID);

    if (!accessToken || !memberId) {
      return Promise.reject(response);
    }

    // ───────────── NEW: VALIDATE BEFORE REFRESH ─────────────

    const isExpired = await validateToken(accessToken);

    // ❌ If NOT expired → don't refresh
    if (!isExpired) {
      return response;
    }

    // ───────────── REFRESH FLOW (UNCHANGED) ─────────────

    if (!memberId) {
      clearAndRedirect();
      return Promise.reject(response);
    }

    try {
      const res = await axios.post(`${AUTH_URL}/auth/sso/refresh`, {
        flag: "ssoRefresh",
        parameters: {
          ssoRequest: {
            authorizationCode: memberId, // UUID
          },
        },
      });

      const body = res.data?.body;

      if (!body?.accessToken) {
        throw new Error("Refresh failed");
      }

      // ✅ Store new tokens
      localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, body.accessToken);
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, body.refreshToken);

      // 🔁 Retry original request
      originalRequest.headers.Authorization = body.accessToken;

      return axiosInstance(originalRequest);
    } catch (error) {
      console.error("Refresh failed:", error);
      clearAndRedirect();
      return Promise.reject(error);
    }
  },
  (error) => Promise.reject(error),
);

// ───────────── HELPERS ─────────────
export function clearAndRedirect() {
  localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.SSO_MEMBER_ID);
  localStorage.removeItem("memberId");
  sessionStorage.removeItem("ssoLoggedIn");

  window.location.href = "/";
}